import { useEffect } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useToast } from '@/hooks/use-toast';

interface AudioRecordButtonProps {
  onTranscription: (text: string) => void;
  disabled?: boolean;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AudioRecordButton({ onTranscription, disabled }: AudioRecordButtonProps) {
  const { isRecording, isTranscribing, duration, startRecording, stopRecording, error } = useAudioRecorder();
  const { toast } = useToast();

  useEffect(() => {
    if (!error) return;

    toast({
      title: 'Audio no reconocido',
      description: error,
      variant: 'destructive',
    });
  }, [error, toast]);

  const handleClick = async () => {
    if (disabled) return;

    if (isRecording) {
      const text = await stopRecording();
      if (text && text !== '[Audio no reconocido]') {
        onTranscription(text);
      } else if (text === '[Audio no reconocido]') {
        toast({
          title: 'Audio no reconocido',
          description: 'No se pudo transcribir el audio. Intenta hablar más claro.',
          variant: 'destructive',
        });
      }
    } else {
      try {
        await startRecording();
      } catch {
        toast({
          title: 'Micrófono no disponible',
          description: 'Permite el acceso al micrófono en la configuración del navegador.',
          variant: 'destructive',
        });
      }
    }
  };

  if (isTranscribing) {
    return (
      <button
        disabled
        className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-muted text-muted-foreground cursor-wait"
        title="Transcribiendo..."
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-xs font-medium hidden sm:inline">Transcribiendo...</span>
      </button>
    );
  }

  if (isRecording) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors border border-red-200"
        title="Parar grabación"
      >
        <Square className="h-4 w-4 fill-current" />
        <span className="text-xs font-medium tabular-nums">{formatDuration(duration)}</span>
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className="p-2.5 rounded-lg hover:bg-[#E2F1EF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      title="Grabar audio"
    >
      <Mic className="h-5 w-5 text-[#002B5B]/60" />
    </button>
  );
}
